'use client';

import { useState, useEffect } from 'react';
import SeasonCard from './SeasonCard';
import InfoSection from './InfoSection';
import RegulationCard from './RegulationCard';
import EmergencyContact from './EmergencyContact';
import TravelTip from './TravelTip';
import Icon from '@/components/ui/AppIcon';

interface Season {
  name: string;
  months: string;
  waterTemp: string;
  visibility: string;
  marineLife: string[];
  conditions: string;
  icon: string;
  color: string;
}

interface InfoItem {
  icon: string;
  title: string;
  description: string;
}

interface Regulation {
  title: string;
  description: string;
  icon: string;
  type: 'requirement' | 'restriction' | 'guideline';
}

interface Contact {
  name: string;
  phone: string;
  available: string;
  icon: string;
}

interface Tip {
  category: string;
  title: string;
  description: string;
  icon: string;
}

const CyprusDivingInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeTab, setActiveTab] = useState('seasons');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const seasons: Season[] = [
  {
    name: "Spring",
    months: "March - May",
    waterTemp: "18–24°C",
    visibility: "15–25 m",
    marineLife: ["Octopus", "Groupers", "Sea Bream", "Moray Eels"],
    conditions: "Calm seas, warming waters, good for beginners",
    icon: "spring",
    color: "border-secondary"
  },
  {
    name: "Summer",
    months: "June - August",
    waterTemp: "24–28°C",
    visibility: "25–35 m",
    marineLife: ["Barracuda", "Tuna", "Damselfish", "Parrotfish"],
    conditions: "Warm waters, peak season, occasional plankton blooms",
    icon: "summer",
    color: "border-warning"
  },
  {
    name: "Autumn",
    months: "September - November",
    waterTemp: "20–26°C",
    visibility: "25–30 m",
    marineLife: ["Rays", "Seahorses", "Nudibranchs", "Cuttlefish"],
    conditions: "Warm water, fewer crowds, very stable conditions",
    icon: "autumn",
    color: "border-accent"
  },
  {
    name: "Winter",
    months: "December - February",
    waterTemp: "15–18°C",
    visibility: "10–25 m",
    marineLife: ["Groupers", "Amberjack", "Dusky Grouper", "Conger Eels"],
    conditions: "Cooler water, changeable sea state, fewer divers",
    icon: "winter",
    color: "border-primary"
  }];


  const equipmentInfo: InfoItem[] = [
  {
    icon: "CubeIcon",
    title: "Wetsuit Requirements",
    description: "5mm wetsuit recommended for spring/autumn, 3mm for summer. 7mm or drysuit for winter diving. Most dive centres provide rental equipment."
  },
  {
    icon: "ShieldCheckIcon",
    title: "Certification Levels",
    description: "Open Water certification minimum. Advanced Open Water recommended for wreck diving. Technical diving certifications required for deep wrecks beyond 40m."
  },
  {
    icon: "WrenchScrewdriverIcon",
    title: "Equipment Rental",
    description: "Full equipment rental available at all dive centres. Average cost: €30-50 per day. BCD, regulators, and tanks maintained to EU standards."
  },
  {
    icon: "CameraIcon",
    title: "Underwater Photography",
    description: "Excellent conditions for photography year-round. Camera rental available at major centres. GoPro mounts and underwater housings widely available."
  },
  {
    icon: "ClockIcon",
    title: "Dive Duration",
    description: "Typical dive duration 40-50 minutes. Two-tank dives common. Surface intervals 60-90 minutes. Night dives available at selected sites."
  },
  {
    icon: "UserGroupIcon",
    title: "Group Sizes",
    description: "Maximum 6 divers per instructor for recreational diving. Private guiding available. Buddy diving permitted for certified divers with dive centre approval."
  }];


  const regulations: Regulation[] = [
  {
    title: "Valid Diving Certification",
    description: "All divers must present valid certification cards from recognized agencies (PADI, SSI, CMAS, BSAC, etc.). Dive centres may request logbook verification depending on the dive profile and site requirements.",
    icon: "DocumentCheckIcon",
    type: "requirement"
  },
  {
    title: "Marine Protected Areas",
    description: "No touching, collecting, or disturbing marine life. Anchoring prohibited in protected zones. Maintain minimum 3m distance from seabed in sensitive areas.",
    icon: "ShieldExclamationIcon",
    type: "restriction"
  },
  {
    title: "Dive Flag Requirements",
    description: "Surface marker buoys mandatory for all dives. Dive boats must display international dive flag. SMB deployment required during safety stops.",
    icon: "FlagIcon",
    type: "requirement"
  },
  {
    title: "Depth Limitations",
    description: "Recreational diving limited to 40m. Technical and overhead-environment diving is subject to dive centre policies and applicable local site restrictions.",
    icon: "ArrowDownCircleIcon",
    type: "restriction"
  },
  {
    title: "Environmental Guidelines",
    description: "Take only photos, leave only bubbles. No feeding fish. Proper buoyancy control to prevent reef damage. Remove any trash encountered underwater.",
    icon: "GlobeAltIcon",
    type: "guideline"
  },
  {
    title: "Dive Planning",
    description: "Dive plan must be filed with dive centre. Buddy system mandatory. Safety stops recommended for all dives. Surface interval calculations required.",
    icon: "ClipboardDocumentCheckIcon",
    type: "guideline"
  }];


  const emergencyContacts: Contact[] = [
  {
    name: "Cyprus Emergency Services",
    phone: "112",
    available: "24/7 General Emergency",
    icon: "PhoneIcon"
  },
  {
    name: "Paphos General Hospital – Hyperbaric Clinic",
    phone: "Information: +357 26 803114 | Appointments: +357 26 803486",
    available: "Hyperbaric & diving medical facility",
    icon: "MedicalIcon"
  },
  {
    name: "Ammochostos (Famagusta) General Hospital – Hyperbaric Clinic",
    phone: "Information: +357 23 200000 | Appointments: +357 23 200085",
    available: "Hyperbaric & diving medical facility",
    icon: "MedicalIcon"
  },
  {
    name: "Cyprus Coastguard",
    phone: "1441",
    available: "24/7 Marine Emergency",
    icon: "LifebuoyIcon"
  },
  {
    name: "DAN Europe Hotline",
    phone: "+39 06 4211 8685",
    available: "24/7 Diving Emergency",
    icon: "HeartIcon"
  }];


  const travelTips: Tip[] = [
  {
    category: "Currency",
    title: "Currency & Payments",
    description: "Cyprus uses the Euro (€). Credit and debit cards widely accepted at dive centres, restaurants and hotels. ATMs available in all coastal towns. Small cash amounts useful for harbour tavernas and local shops.",
    icon: "CurrencyEuroIcon"
  },
  {
    category: "Transport",
    title: "Transport & Getting Around",
    description: "Car rental strongly recommended — most dive sites require early-morning departures and are not served by public transport. Driving is on the left. International license required. Taxis available but expensive for repeated dive trips.",
    icon: "TruckIcon"
  },
  {
    category: "Language",
    title: "Language & Communication",
    description: "Greek is the main language, but English is very widely spoken in all dive centres, coastal areas and tourist zones. Dive briefings are typically conducted in English. Basic Greek phrases appreciated but not necessary.",
    icon: "ChatBubbleLeftRightIcon"
  },
  {
    category: "Health",
    title: "Medical Facilities",
    description: "Modern hospitals in Paphos, Limassol, Larnaca and Nicosia. Two hyperbaric chambers available (see Regulations & Safety tab). Travel insurance with dive coverage mandatory. EU health card (EHIC) accepted for EU citizens.",
    icon: "HeartIcon"
  },
  {
    category: "Etiquette",
    title: "Local Etiquette",
    description: "Cypriots are friendly and relaxed. Most harbour tavernas welcome divers straight from the boat (wet hair, dive shirts). Tipping 10% appreciated for dive guides and boat crews. Respect marine protected areas and follow dive centre guidance.",
    icon: "HandRaisedIcon"
  },
  {
    category: "Activities",
    title: "Non-Diving Activities",
    description: "Kourion (Curium) amphitheatre near Limassol, Paphos Archaeological Park, Troodos mountain villages and short hikes, Ayia Napa and Protaras coastal walks and boat trips. Wine routes and traditional meze restaurants throughout the island.",
    icon: "SparklesIcon"
  }];


  const tabs = [
  { id: 'seasons', label: 'Diving Seasons', icon: 'CalendarIcon' },
  { id: 'equipment', label: 'Equipment & Planning', icon: 'CubeIcon' },
  { id: 'regulations', label: 'Regulations & Safety', icon: 'ShieldCheckIcon' },
  { id: 'travel', label: 'Travel Tips', icon: 'InformationCircleIcon' }];


  if (!isHydrated) {
    return (
      <div style={{ backgroundColor: '#0B1622' }} className="min-h-screen">
        <div className="h-16 lg:h-20" />
        <div className="container mx-auto px-4 py-12">
          <div className="animate-pulse space-y-8">
            <div style={{ backgroundColor: 'rgba(255,255,255,0.06)' }} className="h-12 rounded w-3/4 mx-auto" />
            <div style={{ backgroundColor: 'rgba(255,255,255,0.06)' }} className="h-64 rounded" />
          </div>
        </div>
      </div>);

  }

  return (
    <div style={{ backgroundColor: '#0B1622' }} className="min-h-screen">
      {/* Hero Section */}
      <section style={{ backgroundColor: '#0E1C2B', borderBottom: '1px solid rgba(111,211,224,0.1)' }} className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 style={{ color: '#EAF4F8' }} className="text-4xl md:text-5xl lg:text-6xl font-headline font-bold mb-6">
              Cyprus Diving Guide &amp; Safety Information
            </h1>
            <p style={{ color: '#C8DDE6' }} className="text-lg md:text-xl leading-relaxed">
              Plan your scuba diving in Cyprus with up-to-date, practical information on diving{' '}
              <span className="inline-flex items-center gap-1">
                <Icon name="CalendarIcon" size={18} style={{ color: '#6FD3E0' }} />
                <span style={{ color: '#EAF4F8' }} className="font-medium">seasons</span>
              </span>
              ,{' '}
              <span className="inline-flex items-center gap-1">
                <Icon name="CubeIcon" size={18} style={{ color: '#6FD3E0' }} />
                <span style={{ color: '#EAF4F8' }} className="font-medium">equipment</span>
              </span>
              ,{' '}
              <span className="inline-flex items-center gap-1">
                <Icon name="DocumentCheckIcon" size={18} style={{ color: '#6FD3E0' }} />
                <span style={{ color: '#EAF4F8' }} className="font-medium">certification</span>
              </span>
              {' '}requirements, local{' '}
              <span className="inline-flex items-center gap-1">
                <Icon name="ShieldCheckIcon" size={18} style={{ color: '#6FD3E0' }} />
                <span style={{ color: '#EAF4F8' }} className="font-medium">regulations</span>
              </span>
              {' '}and{' '}
              <span className="inline-flex items-center gap-1">
                <Icon name="ExclamationTriangleIcon" size={18} style={{ color: '#f87171' }} />
                <span style={{ color: '#EAF4F8' }} className="font-medium">emergency</span>
              </span>
              {' '}procedures. This independent guide is created by a Cyprus-based diving instructor and helps recreational and technical divers prepare safely for diving in Cyprus.
            </p>
          </div>
        </div>
      </section>

      {/* Tab Navigation */}
      <div style={{ backgroundColor: '#0B1622', borderBottom: '1px solid rgba(111,211,224,0.12)' }} className="sticky top-16 lg:top-20 z-40">
        <div className="container mx-auto px-4">
          <div className="flex overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={
                  activeTab === tab.id
                    ? { color: '#6FD3E0', borderBottomColor: '#6FD3E0', backgroundColor: 'rgba(111,211,224,0.06)', borderBottomWidth: '2px', borderBottomStyle: 'solid' }
                    : { color: '#C8DDE6', borderBottomColor: 'transparent', borderBottomWidth: '2px', borderBottomStyle: 'solid' }
                }
                className="flex items-center space-x-2 px-6 py-4 font-medium whitespace-nowrap transition-all duration-300 hover:opacity-80"
              >
                <Icon name={tab.icon as any} size={20} />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Seasons Tab */}
      {activeTab === 'seasons' && (
        <>
          <section style={{ backgroundColor: '#0B1622' }} className="py-12">
            <div className="container mx-auto px-4">
              <div className="max-w-3xl mx-auto text-center mb-12">
                <h2 style={{ color: '#EAF4F8' }} className="text-3xl lg:text-4xl font-headline font-bold mb-4">
                  Diving Seasons in Cyprus
                </h2>
                <p style={{ color: '#C8DDE6' }} className="text-lg leading-relaxed">
                  Cyprus offers excellent diving conditions year-round, with each season bringing unique characteristics and marine life encounters.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
                {seasons.map((season, index) => <SeasonCard key={index} season={season} />)}
              </div>
            </div>
          </section>

          <section style={{ backgroundColor: '#0E1C2B', borderTop: '1px solid rgba(111,211,224,0.08)' }} className="py-12">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h3 style={{ color: '#EAF4F8' }} className="text-2xl font-headline font-bold mb-6 text-center">
                  Best Time to Dive
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div style={{ backgroundColor: '#0F2234', border: '1px solid rgba(111,211,224,0.12)' }} className="rounded-lg p-6 text-center">
                    <div style={{ backgroundColor: 'rgba(111,211,224,0.1)' }} className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon name="EyeIcon" size={32} style={{ color: '#6FD3E0' }} />
                    </div>
                    <h4 style={{ color: '#EAF4F8' }} className="font-headline font-semibold mb-2">Best Visibility</h4>
                    <p style={{ color: '#6FD3E0' }} className="text-sm">September – November</p>
                    <p style={{ color: '#C8DDE6' }} className="text-xs mt-2">Crystal clear waters up to 40m visibility</p>
                  </div>
                  <div style={{ backgroundColor: '#0F2234', border: '1px solid rgba(111,211,224,0.12)' }} className="rounded-lg p-6 text-center">
                    <div style={{ backgroundColor: 'rgba(255,138,61,0.1)' }} className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon name="FireIcon" size={32} style={{ color: '#FF8A3D' }} />
                    </div>
                    <h4 style={{ color: '#EAF4F8' }} className="font-headline font-semibold mb-2">Warmest Waters</h4>
                    <p style={{ color: '#FF8A3D' }} className="text-sm">July – September</p>
                    <p style={{ color: '#C8DDE6' }} className="text-xs mt-2">Comfortable 26–28°C temperatures</p>
                  </div>
                  <div style={{ backgroundColor: '#0F2234', border: '1px solid rgba(111,211,224,0.12)' }} className="rounded-lg p-6 text-center">
                    <div style={{ backgroundColor: 'rgba(111,211,224,0.1)' }} className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon name="UserGroupIcon" size={32} style={{ color: '#6FD3E0' }} />
                    </div>
                    <h4 style={{ color: '#EAF4F8' }} className="font-headline font-semibold mb-2">Fewer Crowds</h4>
                    <p style={{ color: '#6FD3E0' }} className="text-sm">November – March</p>
                    <p style={{ color: '#C8DDE6' }} className="text-xs mt-2">Peaceful diving with excellent conditions</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Equipment Tab */}
      {activeTab === 'equipment' && (
        <InfoSection title="Equipment &amp; Planning Information" items={equipmentInfo} />
      )}

      {/* Regulations Tab */}
      {activeTab === 'regulations' && (
        <>
          <section style={{ backgroundColor: '#0B1622' }} className="py-12">
            <div className="container mx-auto px-4">
              <div className="max-w-3xl mx-auto text-center mb-12">
                <h2 style={{ color: '#EAF4F8' }} className="text-3xl lg:text-4xl font-headline font-bold mb-4">
                  Diving Regulations &amp; Safety
                </h2>
                <p style={{ color: '#C8DDE6' }} className="text-lg leading-relaxed">
                  Cyprus follows EU diving standards and maintains strict environmental protection measures to preserve its underwater heritage.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto mb-12">
                {regulations.map((regulation, index) => <RegulationCard key={index} regulation={regulation} />)}
              </div>
              <div className="max-w-4xl mx-auto">
                <EmergencyContact contacts={emergencyContacts} />
              </div>
            </div>
          </section>

          <section id="conservation" style={{ backgroundColor: '#0E1C2B', borderTop: '1px solid rgba(111,211,224,0.08)' }} className="py-12 scroll-mt-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-8">
                  <h3 style={{ color: '#EAF4F8' }} className="text-2xl lg:text-3xl font-headline font-bold mb-4">
                    Marine Conservation in Cyprus
                  </h3>
                  <p style={{ color: '#C8DDE6' }} className="text-lg leading-relaxed">
                    Cyprus waters are home to important Mediterranean ecosystems that require careful protection and responsible diving practices.
                  </p>
                </div>

                <div style={{ backgroundColor: '#0F2234', border: '1px solid rgba(111,211,224,0.12)' }} className="rounded-xl p-8 mb-6 shadow-md">
                  <div className="flex items-start space-x-4 mb-6">
                    <div className="flex-shrink-0">
                      <div style={{ backgroundColor: 'rgba(34,197,94,0.1)' }} className="w-12 h-12 rounded-full flex items-center justify-center">
                        <Icon name="GlobeAltIcon" size={24} style={{ color: '#4ade80' }} />
                      </div>
                    </div>
                    <div>
                      <h4 style={{ color: '#EAF4F8' }} className="text-xl font-headline font-bold mb-3">Protected Marine Areas</h4>
                      <p style={{ color: '#C8DDE6' }} className="leading-relaxed mb-4">
                        Cyprus has designated several protected coastal zones to preserve unique Mediterranean ecosystems. These areas support endangered species including loggerhead and green sea turtles, Mediterranean monk seals, and extensive Posidonia oceanica seagrass meadows.
                      </p>
                      <p style={{ color: '#C8DDE6' }} className="leading-relaxed">
                        Protected zones include sections of Lara Bay, Akamas Peninsula, Cape Greco Marine Park, and designated turtle nesting beaches along the southern coast.
                      </p>
                    </div>
                  </div>
                </div>

                <div style={{ backgroundColor: '#0F2234', border: '2px solid rgba(111,211,224,0.2)' }} className="rounded-xl p-6 mb-6 shadow-md">
                  <div className="flex items-center space-x-3 mb-4">
                    <div style={{ backgroundColor: 'rgba(111,211,224,0.1)' }} className="w-10 h-10 rounded-full flex items-center justify-center">
                      <Icon name="InformationCircleIcon" size={20} style={{ color: '#6FD3E0' }} />
                    </div>
                    <h4 style={{ color: '#EAF4F8' }} className="text-lg font-headline font-bold">What Divers Should Know in Cyprus</h4>
                  </div>
                  <ul className="space-y-3">
                    {[
                      { icon: 'XCircleIcon', color: '#f87171', text: <><strong style={{ color: '#EAF4F8' }}>Touching or collecting marine life is prohibited</strong> — this includes shells, rocks, and any living organisms</> },
                      { icon: 'ShieldExclamationIcon', color: '#FF8A3D', text: <><strong style={{ color: '#EAF4F8' }}>Anchoring is restricted in protected zones</strong> — dive boats must use designated mooring buoys where available</> },
                      { icon: 'ArrowTrendingUpIcon', color: '#6FD3E0', text: <><strong style={{ color: '#EAF4F8' }}>Buoyancy control is essential</strong> — maintain neutral buoyancy near reefs, seagrass beds, and wreck structures</> },
                      { icon: 'MapPinIcon', color: '#4ade80', text: <><strong style={{ color: '#EAF4F8' }}>Respect marked conservation zones</strong> — follow dive centre briefings and observe seasonal restrictions</> },
                      { icon: 'UserGroupIcon', color: '#6FD3E0', text: <><strong style={{ color: '#EAF4F8' }}>Always dive with certified operators</strong> — local dive centres provide essential site-specific guidance and safety protocols</> },
                    ].map((item, i) => (
                      <li key={i} className="flex items-start space-x-3">
                        <Icon name={item.icon as any} size={20} style={{ color: item.color }} className="mt-0.5 flex-shrink-0" />
                        <span style={{ color: '#C8DDE6' }} className="leading-relaxed">{item.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div style={{ backgroundColor: '#0F2234', border: '1px solid rgba(111,211,224,0.12)' }} className="rounded-xl p-6 shadow-md">
                    <div className="flex items-center space-x-2 mb-4">
                      <Icon name="CheckCircleIcon" size={20} style={{ color: '#4ade80' }} />
                      <h5 style={{ color: '#EAF4F8' }} className="font-headline font-semibold">Responsible Diving Practices</h5>
                    </div>
                    <ul className="space-y-2 text-sm">
                      {['Maintain neutral buoyancy at all times', 'Never touch or disturb marine life', 'Collect any trash you encounter underwater', 'Use reef-safe sunscreen products', 'Secure all equipment to prevent dragging', 'Maintain safe distance from sea turtles (minimum 3m)'].map((item, i) => (
                        <li key={i} className="flex items-start space-x-2">
                          <Icon name="CheckCircleIcon" size={16} style={{ color: '#4ade80' }} className="mt-1 flex-shrink-0" />
                          <span style={{ color: '#C8DDE6' }}>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div style={{ backgroundColor: '#0F2234', border: '1px solid rgba(111,211,224,0.12)' }} className="rounded-xl p-6 shadow-md">
                    <div className="flex items-center space-x-2 mb-4">
                      <Icon name="SparklesIcon" size={20} style={{ color: '#6FD3E0' }} />
                      <h5 style={{ color: '#EAF4F8' }} className="font-headline font-semibold">Conservation Initiatives</h5>
                    </div>
                    <ul className="space-y-2 text-sm">
                      {['Artificial reef projects for marine habitat restoration', 'Sea turtle nesting site monitoring and protection', 'Regular underwater cleanup events organized by dive centres', 'Marine research and monitoring programs', 'Posidonia seagrass meadow protection zones', 'Collaboration with local fisheries for sustainable practices'].map((item, i) => (
                        <li key={i} className="flex items-start space-x-2">
                          <Icon name="CheckCircleIcon" size={16} style={{ color: '#6FD3E0' }} className="mt-1 flex-shrink-0" />
                          <span style={{ color: '#C8DDE6' }}>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Travel Tips Tab */}
      {activeTab === 'travel' && (
        <section style={{ backgroundColor: '#0B1622' }} className="py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-8">
              <h2 style={{ color: '#EAF4F8' }} className="text-3xl lg:text-4xl font-headline font-bold mb-4">
                Travel Tips for Divers in Cyprus
              </h2>
              <p style={{ color: '#C8DDE6' }} className="text-lg leading-relaxed">
                Practical, local advice to help you plan your diving trip to Cyprus.
              </p>
            </div>

            <div className="max-w-4xl mx-auto mb-10">
              <div style={{ backgroundColor: 'rgba(255,138,61,0.08)', borderLeft: '4px solid #FF8A3D' }} className="rounded-r-lg p-5 flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <Icon name="ExclamationTriangleIcon" size={24} style={{ color: '#FF8A3D' }} />
                </div>
                <div>
                  <p style={{ color: '#EAF4F8' }} className="text-sm font-medium">
                    <strong>Tip for divers:</strong> <span style={{ color: '#C8DDE6' }}>Most dive sites in Cyprus are easiest to access by car. Public transport is limited for early-morning dive schedules.</span>
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto mb-12">
              {travelTips.map((tip, index) => <TravelTip key={index} tip={tip} />)}
            </div>

            <div className="max-w-4xl mx-auto mb-10">
              <div style={{ backgroundColor: '#0E1C2B', border: '2px solid rgba(111,211,224,0.2)' }} className="rounded-xl p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <div style={{ backgroundColor: '#6FD3E0' }} className="w-12 h-12 rounded-full flex items-center justify-center">
                    <Icon name="LightBulbIcon" size={24} style={{ color: '#0B1622' }} />
                  </div>
                  <h3 style={{ color: '#EAF4F8' }} className="text-2xl font-headline font-bold">
                    Local Insider Tips for Divers in Cyprus
                  </h3>
                </div>
                <div className="space-y-4">
                  {[
                    { strong: 'Shoulder season tip:', text: 'April–May and October–November offer calmer dive sites, easier logistics, and fewer crowds — ideal for relaxed diving.' },
                    { strong: 'Harbour tavernas after diving:', text: 'Common meeting points for divers. Most are very relaxed about wet hair and dive clothes straight after dives.' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start space-x-3">
                      <Icon name="CheckCircleIcon" size={20} style={{ color: '#6FD3E0' }} className="mt-1 flex-shrink-0" />
                      <p style={{ color: '#C8DDE6' }} className="leading-relaxed">
                        <strong style={{ color: '#EAF4F8' }}>{item.strong}</strong> {item.text}
                      </p>
                    </div>
                  ))}
                  <div className="flex items-start space-x-3">
                    <Icon name="CheckCircleIcon" size={20} style={{ color: '#6FD3E0' }} className="mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <p style={{ color: '#C8DDE6' }} className="leading-relaxed mb-2"><strong style={{ color: '#EAF4F8' }}>Local food after diving:</strong></p>
                      <ul className="ml-4 space-y-1 text-sm">
                        {[['Halloumi cheese', 'grilled or fried, a Cyprus staple'], ['Meze', 'shared small dishes, perfect for groups'], ['Fresh grilled fish', 'caught locally, served whole']].map(([name, desc], i) => (
                          <li key={i} className="flex items-center space-x-2">
                            <span style={{ backgroundColor: '#6FD3E0' }} className="w-1.5 h-1.5 rounded-full"></span>
                            <span style={{ color: '#C8DDE6' }}><strong style={{ color: '#EAF4F8' }}>{name}</strong> — {desc}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="max-w-4xl mx-auto mb-10">
              <div style={{ backgroundColor: '#0E1C2B', border: '1px solid rgba(111,211,224,0.12)' }} className="rounded-lg p-8">
                <h3 style={{ color: '#EAF4F8' }} className="text-2xl font-headline font-bold mb-6 flex items-center space-x-3">
                  <Icon name="MapPinIcon" size={28} style={{ color: '#6FD3E0' }} />
                  <span>Combining Diving with Other Activities</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 style={{ color: '#EAF4F8' }} className="font-semibold mb-3 flex items-center space-x-2">
                      <Icon name="BuildingLibraryIcon" size={18} style={{ color: '#6FD3E0' }} />
                      <span>Cultural &amp; Historical Sites</span>
                    </h4>
                    <ul className="space-y-2 text-sm">
                      {['Kourion (Curium) amphitheatre near Limassol', 'Paphos Archaeological Park with Roman mosaics', 'Troodos villages and short mountain hikes', 'Medieval castles and Byzantine monasteries'].map((item, i) => (
                        <li key={i} className="flex items-start space-x-2">
                          <span style={{ color: '#6FD3E0' }} className="mt-1">•</span>
                          <span style={{ color: '#C8DDE6' }}>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 style={{ color: '#EAF4F8' }} className="font-semibold mb-3 flex items-center space-x-2">
                      <Icon name="SunIcon" size={18} style={{ color: '#FF8A3D' }} />
                      <span>Coastal &amp; Nature Activities</span>
                    </h4>
                    <ul className="space-y-2 text-sm">
                      {['Ayia Napa and Protaras coastal walks', 'Boat trips with snorkeling and swimming stops', 'Wine routes through traditional villages', 'Nature trails and birdwatching in Akamas Peninsula'].map((item, i) => (
                        <li key={i} className="flex items-start space-x-2">
                          <span style={{ color: '#6FD3E0' }} className="mt-1">•</span>
                          <span style={{ color: '#C8DDE6' }}>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="max-w-4xl mx-auto mb-10 flex justify-center">
              <img
                src="/assets/images/openart-image_ENIQT6-4_1769928765025_raw-1769928829950.jpg"
                alt="Cyprus Diving octopus mascot in travel mode with suitcase"
                className="w-64 h-auto rounded-lg shadow-lg"
              />
            </div>
          </div>
        </section>
      )}

      {/* Call to Action */}
      <section style={{ backgroundColor: '#0E1C2B', borderTop: '1px solid rgba(111,211,224,0.1)' }} className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div style={{ backgroundColor: 'rgba(111,211,224,0.06)', border: '2px solid rgba(111,211,224,0.2)' }} className="rounded-xl p-8">
              <div className="flex items-start space-x-4 mb-6">
                <div className="flex-shrink-0">
                  <div style={{ backgroundColor: '#6FD3E0' }} className="w-12 h-12 rounded-full flex items-center justify-center">
                    <Icon name="PencilSquareIcon" size={24} style={{ color: '#0B1622' }} />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 style={{ color: '#EAF4F8' }} className="text-2xl font-headline font-bold mb-3">
                    Help us keep this guide accurate
                  </h3>
                  <p style={{ color: '#C8DDE6' }} className="leading-relaxed mb-6">
                    Are you a local diver, instructor or dive centre in Cyprus? You can suggest corrections or submit updated information to help keep this independent guide accurate and current.
                  </p>
                  <a
                    href="/homepage#community"
                    style={{ backgroundColor: '#6FD3E0', color: '#0B1622' }}
                    className="inline-flex items-center space-x-2 px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity"
                  >
                    <Icon name="PaperAirplaneIcon" size={20} />
                    <span>Submit information</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section style={{ background: 'linear-gradient(to right, #0E1C2B, #0F2234)' }} className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 style={{ color: '#EAF4F8' }} className="text-3xl lg:text-4xl font-headline font-bold mb-6">
              Ready to Explore Cyprus Underwater?
            </h2>
            <p style={{ color: '#C8DDE6' }} className="text-xl mb-8 leading-relaxed">
              Start planning your diving adventure with our comprehensive guides to dive sites, marine life, and local dive centres.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="/regional-dive-sites"
                style={{ backgroundColor: '#6FD3E0', color: '#0B1622' }}
                className="inline-flex items-center space-x-2 px-8 py-4 rounded-lg font-semibold hover:opacity-90 transition-opacity shadow-lg"
              >
                <Icon name="MapIcon" size={20} />
                <span>Explore Dive Sites</span>
              </a>
              <a
                href="/dive-centres-directory"
                style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: '#EAF4F8', border: '2px solid rgba(111,211,224,0.3)' }}
                className="inline-flex items-center space-x-2 px-8 py-4 rounded-lg font-semibold hover:opacity-90 transition-opacity"
              >
                <Icon name="BuildingOfficeIcon" size={20} />
                <span>Find Dive Centres</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CyprusDivingInteractive;